import { ChevronLeft, ChevronRight } from './icons';

export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  /** Total number of pages (>= 1). */
  totalPages: number;
  /** Total item count across all pages — drives the "Showing X–Y of N" label. */
  totalItems: number;
  /** Items per page — drives the range label. */
  perPage: number;
  onPageChange: (page: number) => void;
  /** Max numbered buttons to render (windowed around the current page). */
  maxButtons?: number;
}

const pagerBtn =
  'grid h-[34px] min-w-[34px] place-items-center rounded-sm px-1.5 text-[14px] font-semibold transition-colors';

/**
 * Shared pagination control for every admin list/table — design system v2
 * `.table-foot` + `.pager`: a muted count on the left and 34px page buttons on
 * the right, the current page filled with the accent. Stateless — the parent
 * owns `page`.
 */
export function Pagination({
  page,
  totalPages,
  totalItems,
  perPage,
  onPageChange,
  maxButtons = 5,
}: PaginationProps) {
  if (totalItems === 0) return null;

  // Window the numbered buttons around the current page so large datasets don't
  // render hundreds of buttons.
  const half = Math.floor(maxButtons / 2);
  let start = Math.max(1, page - half);
  const end = Math.min(totalPages, start + maxButtons - 1);
  start = Math.max(1, end - maxButtons + 1);
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);

  const from = Math.min((page - 1) * perPage + 1, totalItems);
  const to = Math.min(page * perPage, totalItems);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-surface-line px-4 py-3">
      <p className="font-tabular text-[13.5px] text-ink-500">
        Showing {from}–{to} of {totalItems}
      </p>
      <nav aria-label="Pagination" className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Previous page"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page === 1}
          className={`${pagerBtn} text-ink-700 hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent`}
        >
          <ChevronLeft size={16} />
        </button>
        {pages.map((pg) => (
          <button
            key={pg}
            type="button"
            onClick={() => onPageChange(pg)}
            aria-current={page === pg ? 'page' : undefined}
            className={`${pagerBtn} font-tabular ${
              page === pg ? 'bg-admin text-white' : 'text-ink-700 hover:bg-surface-2'
            }`}
          >
            {pg}
          </button>
        ))}
        <button
          type="button"
          aria-label="Next page"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page === totalPages || totalPages === 0}
          className={`${pagerBtn} text-ink-700 hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent`}
        >
          <ChevronRight size={16} />
        </button>
      </nav>
    </div>
  );
}
